import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { getLoginUrl } from "@/const";
import { useState } from "react";
import {
  Building2,
  FileText,
  Calculator,
  TrendingUp,
  Shield,
  Clock,
  CheckCircle2,
  Zap,
  Database,
  Lock,
  MapPin,
  Award,
  Search,
  Upload,
  Eye,
  Check,
  ArrowRight,
  FileCheck,
  Users,
  Globe,
  Sparkles,
  Crown,
  Star,
} from "lucide-react";
import { Link } from "wouter";
import { SwissAlpsHero } from "@/components/SwissAlpsHero";

const pricingTiers = [
  {
    name: "Free",
    monthlyPrice: 0,
    yearlyPrice: 0,
    description: "Get started with basic tax management",
    features: [
      "1 company",
      "100 transactions / month",
      "10 AI credits",
      "Basic bank import",
      "Swiss account plan",
    ],
    cta: "Get Started Free",
    highlight: false,
    icon: null,
  },
  {
    name: "Starter",
    monthlyPrice: 29,
    yearlyPrice: 290,
    description: "For small businesses and freelancers",
    features: [
      "3 companies",
      "1'000 transactions / month",
      "100 AI credits",
      "All bank imports",
      "Vendor tracking",
      "Tax reports",
    ],
    cta: "Start Free Trial",
    highlight: false,
    icon: Sparkles,
  },
  {
    name: "Professional",
    monthlyPrice: 79,
    yearlyPrice: 790,
    description: "For accountants and growing businesses",
    features: [
      "10 companies",
      "10'000 transactions / month",
      "500 AI credits",
      "Multi-client management",
      "Zefix integration",
      "Priority support",
      "Bulk document processing",
    ],
    cta: "Start Free Trial",
    highlight: true,
    icon: Crown,
  },
  {
    name: "Enterprise",
    monthlyPrice: 199,
    yearlyPrice: 1990,
    description: "For firms and large organizations",
    features: [
      "Unlimited companies",
      "Unlimited transactions",
      "Unlimited AI credits",
      "Canton admin access",
      "Custom integrations",
      "Dedicated support",
      "SLA guarantee",
      "White-label option",
    ],
    cta: "Contact Sales",
    highlight: false,
    icon: null,
  },
];

function PricingSection() {
  const [isYearly, setIsYearly] = useState(false);

  return (
    <section id="pricing" className="section-spacing bg-muted/30">
      <div className="container">
        <div className="text-center mb-12">
          <Badge variant="secondary" className="mb-4">
            Simple Pricing
          </Badge>
          <h2 className="text-3xl font-bold mb-4">
            Plans for Every Swiss Business
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            Start free. Upgrade when you need more. All plans include Swiss data
            residency.
          </p>

          {/* Billing Toggle */}
          <div className="flex items-center justify-center gap-3">
            <span
              className={`text-sm font-medium ${!isYearly ? "text-foreground" : "text-muted-foreground"}`}
            >
              Monthly
            </span>
            <button
              onClick={() => setIsYearly(!isYearly)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                isYearly ? "bg-primary" : "bg-muted-foreground/30"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 rounded-full bg-background transition-transform ${
                  isYearly ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
            <span
              className={`text-sm font-medium ${isYearly ? "text-foreground" : "text-muted-foreground"}`}
            >
              Yearly
            </span>
            {isYearly && (
              <Badge variant="secondary" className="text-xs">
                Save 2 months
              </Badge>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
          {pricingTiers.map(tier => {
            const TierIcon = tier.icon;
            const price = isYearly ? tier.yearlyPrice : tier.monthlyPrice;
            const perMonth = isYearly
              ? Math.round(tier.yearlyPrice / 12)
              : tier.monthlyPrice;

            return (
              <Card
                key={tier.name}
                className={`relative h-full flex flex-col ${
                  tier.highlight ? "border-primary shadow-lg scale-[1.02]" : ""
                }`}
              >
                {tier.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-primary text-primary-foreground">
                      Most Popular
                    </Badge>
                  </div>
                )}
                <CardHeader className="pb-4">
                  <div className="flex items-center gap-2">
                    {TierIcon && <TierIcon className="h-5 w-5 text-primary" />}
                    <CardTitle className="text-xl">{tier.name}</CardTitle>
                  </div>
                  <CardDescription>{tier.description}</CardDescription>
                </CardHeader>
                <CardContent className="flex-1 flex flex-col">
                  <div className="mb-6">
                    {price === 0 ? (
                      <div className="text-3xl font-bold">Free</div>
                    ) : (
                      <>
                        <div className="flex items-baseline gap-1">
                          <span className="text-3xl font-bold">
                            CHF {perMonth}
                          </span>
                          <span className="text-muted-foreground text-sm">
                            /month
                          </span>
                        </div>
                        {isYearly && (
                          <p className="text-xs text-muted-foreground mt-1">
                            CHF {price.toLocaleString("de-CH")} billed yearly
                          </p>
                        )}
                      </>
                    )}
                  </div>

                  <ul className="space-y-2 mb-6 flex-1">
                    {tier.features.map(feature => (
                      <li key={feature} className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-muted-foreground">
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <a href={getLoginUrl()} className="mt-auto">
                    <Button
                      className={`w-full ${tier.highlight ? "cta-primary" : ""}`}
                      variant={tier.highlight ? "default" : "outline"}
                    >
                      {tier.cta}
                    </Button>
                  </a>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <p className="text-center text-sm text-muted-foreground mt-8">
          All prices in CHF. VAT excluded. 14-day free trial on paid plans.
        </p>
      </div>
    </section>
  );
}

export default function HomeImproved() {
  const { user, isAuthenticated } = useAuth();

  // Visual workflow steps
  const workflowSteps = [
    {
      icon: Upload,
      step: "1",
      title: "Drop Your Documents",
      description: "Bank statements, invoices, contracts - any format",
      detail: "PostFinance, UBS, Credit Suisse, ZKB, Raiffeisen",
    },
    {
      icon: Zap,
      step: "2",
      title: "AI Extracts Everything",
      description: "Google Gemini analyzes and structures your data",
      detail: "Company profile, bank accounts, vendors, transactions",
    },
    {
      icon: Eye,
      step: "3",
      title: "Review & Adjust",
      description: "Preview extracted data before committing",
      detail: "Edit conflicts, verify entities, approve results",
    },
    {
      icon: Check,
      step: "4",
      title: "Commit & Go",
      description: "Your company is ready with full history",
      detail: "Transactions categorized, vendors mapped, tax-ready",
    },
  ];

  const features = [
    {
      icon: FileText,
      title: "Document-First Onboarding",
      description:
        "Skip the forms. Drop bank statements, invoices, and contracts. The platform extracts company details, bank accounts, and vendor relationships automatically.",
      benefit: "From documents to tax-ready in minutes",
    },
    {
      icon: Globe,
      title: "All 26 Swiss Cantons",
      description:
        "Multi-canton support with canton-specific tax rates, configurations, and feature flags. Canton admin roles for regional management.",
      benefit: "One platform for all Swiss jurisdictions",
    },
    {
      icon: Zap,
      title: "Google Gemini AI",
      description:
        "State-of-the-art LLM-powered categorization. Pattern learning from your corrections. Context-aware understanding beyond keywords.",
      benefit: "95% automatic categorization accuracy",
    },
    {
      icon: Search,
      title: "Zefix Integration",
      description:
        "Direct connection to Swiss Commercial Registry. Instant company lookup by name or UID. Automatic profile validation.",
      benefit: "100% accurate company data",
    },
    {
      icon: Calculator,
      title: "Swiss Tax Compliance",
      description:
        "All VAT rates (7.7%, 2.5%, 3.7%), corporate tax, withholding tax. Canton-specific calculations. Swiss GAAP FER reporting.",
      benefit: "Zero compliance errors",
    },
    {
      icon: Shield,
      title: "Swiss Data Residency",
      description:
        "100% hosted in Switzerland (Zurich). No data leaves Swiss/EU jurisdiction. 10-year retention for tax compliance.",
      benefit: "Complete data sovereignty",
    },
  ];

  const trustSignals = [
    { icon: MapPin, text: "Zurich Hosted", detail: "Firebase europe-west6" },
    { icon: Lock, text: "Bank-Grade Security", detail: "SOC 2 compliant" },
    { icon: Database, text: "Zefix Connected", detail: "Official registry" },
    { icon: Award, text: "Swiss Compliant", detail: "GAAP FER ready" },
  ];

  const useCases = [
    {
      title: "For SME Owners",
      icon: Building2,
      benefits: [
        "Drop bank statements, get categorized transactions",
        "Automatic vendor and customer tracking",
        "R&D tax credit identification",
        "Audit-ready reports on demand",
      ],
      highlight: "Save 20+ hours monthly",
    },
    {
      title: "For Accountants",
      icon: Users,
      benefits: [
        "Client onboarding in minutes via document drop",
        "Multi-client management from one dashboard",
        "Pattern learning across your client base",
        "White-label canton support",
      ],
      highlight: "10x faster client setup",
    },
    {
      title: "For CFOs",
      icon: TrendingUp,
      benefits: [
        "Real-time financial visibility",
        "Tax optimization scenario modeling",
        "Multi-entity consolidated views",
        "Instant compliance reporting",
      ],
      highlight: "Data-driven decisions",
    },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="absolute top-0 left-0 right-0 z-50 border-b border-primary/10 bg-background/50 backdrop-blur-md">
        <div className="container flex h-16 items-center justify-between">
          <div className="flex items-center gap-2">
            <Building2 className="h-6 w-6 text-primary" />
            <span className="font-semibold text-lg">Swiss Tax Wizard</span>
          </div>
          <div className="flex items-center gap-4">
            {isAuthenticated ? (
              <Link href="/dashboard">
                <Button>Go to Dashboard</Button>
              </Link>
            ) : (
              <>
                <a href={getLoginUrl()}>
                  <Button variant="ghost">Sign In</Button>
                </a>
                <a href={getLoginUrl()}>
                  <Button className="cta-primary">Get Started Free</Button>
                </a>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <SwissAlpsHero
        title="Drop Documents. Get Tax-Ready."
        subtitle="Upload your bank statements and invoices. AI extracts everything. From raw documents to structured, auditable, Swiss-compliant tax data in minutes."
        isAuthenticated={isAuthenticated}
        userName={user?.name || undefined}
        ctaPrimary={
          isAuthenticated
            ? undefined
            : {
                text: "Start Free",
                href: getLoginUrl(),
              }
        }
        ctaSecondary={
          isAuthenticated
            ? undefined
            : {
                text: "See How It Works",
                href: "#how-it-works",
              }
        }
      />

      {/* Trust Signals Bar */}
      <section className="bg-muted/30 border-y">
        <div className="container py-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {trustSignals.map((signal, index) => {
              const Icon = signal.icon;
              return (
                <div key={index} className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Icon className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <div className="font-semibold text-sm">{signal.text}</div>
                    <div className="text-xs text-muted-foreground">
                      {signal.detail}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works - Visual Workflow */}
      <section id="how-it-works" className="section-spacing">
        <div className="container">
          <div className="text-center mb-12">
            <Badge variant="secondary" className="mb-4">
              Document-First Approach
            </Badge>
            <h2 className="text-3xl font-bold mb-4">
              From Documents to Tax-Ready in 4 Steps
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              No forms to fill. No manual data entry. Just drop your documents.
            </p>
          </div>

          {/* Visual Workflow Diagram */}
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-4 gap-4 relative">
              {/* Connection lines (desktop only) */}
              <div className="hidden md:block absolute top-16 left-[12%] right-[12%] h-0.5 bg-primary/20" />

              {workflowSteps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <div key={index} className="relative">
                    <Card className="text-center h-full border-primary/20 hover:border-primary/40 transition-colors">
                      <CardHeader className="pb-2">
                        <div className="mx-auto h-14 w-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center mb-3 relative z-10">
                          <Icon className="h-7 w-7" />
                        </div>
                        <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          Step {step.step}
                        </div>
                        <CardTitle className="text-lg">{step.title}</CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <p className="text-sm text-muted-foreground mb-2">
                          {step.description}
                        </p>
                        <p className="text-xs text-primary font-medium">
                          {step.detail}
                        </p>
                      </CardContent>
                    </Card>
                    {index < workflowSteps.length - 1 && (
                      <div className="hidden md:flex absolute -right-2 top-16 z-20">
                        <ArrowRight className="h-4 w-4 text-primary/40" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Supported Formats */}
          <div className="mt-12 text-center">
            <p className="text-sm text-muted-foreground mb-4">
              Supports all major Swiss banks
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                "PostFinance",
                "UBS",
                "Credit Suisse",
                "ZKB",
                "Raiffeisen",
                "Migros Bank",
                "Julius Bär",
              ].map(bank => (
                <Badge key={bank} variant="outline" className="px-4 py-2">
                  {bank}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="section-spacing bg-muted/30">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">
              Built for Swiss Business
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Whether you're a small business owner, accountant, or CFO - one
              platform for all your Swiss tax compliance needs.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {useCases.map((useCase, index) => {
              const Icon = useCase.icon;
              return (
                <Card key={index} className="h-full">
                  <CardHeader>
                    <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                      <Icon className="h-6 w-6 text-primary" />
                    </div>
                    <CardTitle>{useCase.title}</CardTitle>
                    <Badge className="w-fit" variant="secondary">
                      {useCase.highlight}
                    </Badge>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {useCase.benefits.map((benefit, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 mt-1" />
                          <span className="text-sm text-muted-foreground">
                            {benefit}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section id="features" className="section-spacing">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Platform Capabilities</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Everything you need for Swiss corporate tax compliance, powered by
              modern AI and built for Swiss requirements.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card
                  key={index}
                  className="feature-card group hover:border-primary/30 transition-colors"
                >
                  <CardHeader>
                    <div className="h-12 w-12 rounded-lg bg-primary/10 group-hover:bg-primary/20 flex items-center justify-center mb-4 transition-colors">
                      <Icon className="h-6 w-6 text-primary" />
                    </div>
                    <CardTitle className="feature-title">
                      {feature.title}
                    </CardTitle>
                    <CardDescription>{feature.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="text-sm font-medium text-primary flex items-center gap-1">
                      <ArrowRight className="h-3 w-3" />
                      {feature.benefit}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Swiss Compliance Emphasis */}
      <section className="section-spacing bg-primary/5">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <Badge variant="secondary" className="mb-4">
                  100% Swiss
                </Badge>
                <h2 className="text-3xl font-bold mb-4">
                  Built for Swiss Complexity
                </h2>
                <p className="text-muted-foreground mb-6">
                  Switzerland has 26 cantons with different tax rates, three
                  official languages, and strict compliance requirements. This
                  isn't a generic tool adapted for Switzerland - it's built from
                  the ground up for Swiss corporate tax.
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="font-medium">
                        Canton-specific tax configurations
                      </span>
                      <p className="text-sm text-muted-foreground">
                        All 26 cantons with their unique rates and rules
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="font-medium">
                        Swiss GAAP FER compliant
                      </span>
                      <p className="text-sm text-muted-foreground">
                        Financial statements that meet auditor requirements
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="font-medium">
                        Multi-language support
                      </span>
                      <p className="text-sm text-muted-foreground">
                        German, French, Italian, and English
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <div>
                      <span className="font-medium">Zurich data center</span>
                      <p className="text-sm text-muted-foreground">
                        Data never leaves Swiss/EU jurisdiction
                      </p>
                    </div>
                  </li>
                </ul>
              </div>
              <div className="bg-background rounded-xl border p-6 shadow-sm">
                <div className="text-center mb-6">
                  <FileCheck className="h-12 w-12 text-primary mx-auto mb-4" />
                  <h3 className="text-xl font-semibold">Try Document Drop</h3>
                  <p className="text-sm text-muted-foreground mt-2">
                    See how fast you can go from bank statements to tax-ready
                    data
                  </p>
                </div>
                <div className="border-2 border-dashed border-primary/30 rounded-lg p-8 text-center bg-primary/5 hover:bg-primary/10 transition-colors cursor-pointer">
                  <Upload className="h-8 w-8 text-primary/60 mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">
                    Drop a bank statement CSV to see it in action
                  </p>
                </div>
                <div className="mt-6 text-center">
                  <a href={getLoginUrl()}>
                    <Button size="lg" className="w-full">
                      Start Free - No Credit Card
                    </Button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <PricingSection />

      {/* Social Proof Section */}
      <section className="section-spacing bg-[var(--muted)]/30">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">
              Trusted by Swiss Businesses
            </h2>
            <p className="text-muted-foreground text-lg">
              From sole proprietors to growing GmbHs
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              {
                quote:
                  "Uploading our bank statements and having everything categorized automatically saved us hours every month.",
                author: "M. Keller",
                role: "CEO, Tech GmbH Zurich",
              },
              {
                quote:
                  "The Swiss account plan templates made it easy to get started. No more guessing which Konto to use.",
                author: "S. Brunner",
                role: "Founder, Design Studio ZH",
              },
              {
                quote:
                  "Finally a tax platform that understands Swiss regulations and keeps our data in Switzerland.",
                author: "R. Meier",
                role: "CFO, Consulting AG",
              },
            ].map((testimonial, i) => (
              <Card key={i} className="text-left">
                <CardContent className="pt-6">
                  <div className="flex gap-1 mb-3">
                    {[...Array(5)].map((_, j) => (
                      <Star
                        key={j}
                        className="h-4 w-4 fill-[var(--primary)] text-[var(--primary)]"
                      />
                    ))}
                  </div>
                  <p className="text-sm text-muted-foreground mb-4 italic">
                    "{testimonial.quote}"
                  </p>
                  <p className="text-sm font-medium">{testimonial.author}</p>
                  <p className="text-xs text-muted-foreground">
                    {testimonial.role}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-spacing">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h2 className="text-3xl font-bold">
              Ready to Simplify Swiss Tax Compliance?
            </h2>
            <p className="text-lg text-muted-foreground">
              Join Swiss businesses who've automated their tax compliance. Drop
              your first document and see the difference.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={getLoginUrl()}>
                <Button size="lg" className="cta-primary">
                  Get Started Free
                </Button>
              </a>
              <Link href="/company-discovery">
                <Button size="lg" variant="outline">
                  Try Document Drop
                </Button>
              </Link>
            </div>
            <p className="text-sm text-muted-foreground">
              Free tier available - No credit card required - Swiss data
              residency
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t mt-auto">
        <div className="container py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-primary" />
              <span className="text-sm text-muted-foreground">
                Swiss Tax Wizard by habitus.net
              </span>
            </div>
            <div className="flex gap-6 text-sm text-muted-foreground">
              <Link
                href="/privacy"
                className="hover:text-foreground transition-colors"
              >
                Privacy
              </Link>
              <Link
                href="/terms"
                className="hover:text-foreground transition-colors"
              >
                Terms
              </Link>
              <a
                href="mailto:info@habitus.net"
                className="hover:text-foreground transition-colors"
              >
                Contact
              </a>
              <span>Zurich, Switzerland</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
